import React from 'react';

import PropTypes from 'prop-types';

import { MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { GetComboboxProps } from '../../types';

export type SearchInputProps = StylableComponent<HTMLInputElement> &
  GetComboboxProps & {
    showIcon?: boolean;
    isOpen: boolean;
    disabled?: boolean;
  };

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      style,
      className,
      getComboboxProps,
      showIcon = true,
      placeholder: placeholderProp,
      isOpen,
      ...props
    },
    ref
  ) => {
    const { T } = useLocale(({ locale }) =>
      require(`../../i18n/${locale}.json`)
    );

    const defaultPlaceholder = isOpen
      ? T.translate('SEARCH')?.toString()
      : T.translate('SELECT')?.toString();

    const placeholder =
      placeholderProp === undefined ? defaultPlaceholder : placeholderProp;

    return (
      <div
        css={[
          {
            display: 'flex',
            flex: 'auto',
            alignItems: 'center',
            overflow: 'hidden',
          },
          style,
        ]}
        className={className}
        {...getComboboxProps()}
      >
        {showIcon && (
          <MagnifyingGlassIcon
            size={20}
            style={[
              {
                marginLeft: -2,
                marginRight: 2,
                color: theme.colors.charcoal500,
              },
            ]}
          />
        )}
        <input
          ref={ref}
          data-testid={'Dropdown__input'}
          css={[
            {
              flexGrow: 1,
              overflow: 'hidden',
              border: 'none',
              outline: 'none',
              color: 'inherit',
              '::placeholder': {
                color: theme.colors.charcoal500,
              },
              backgroundColor: 'transparent',
              ...(!isOpen && {
                cursor: 'pointer',
              }),
            },
            theme.typography.labelMedium,
          ]}
          placeholder={placeholder}
          {...props}
        />
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Input placeholder text */
  placeholder: PropTypes.string,
  /** Function to return props for the input element's parent */
  getComboboxProps: PropTypes.func.isRequired,
  /** Whether to show the search icon */
  showIcon: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
};

export default SearchInput;
